import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, InformationCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
    type?: 'warning' | 'danger' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    message,
    confirmText = '确认',
    cancelText = '取消',
    onConfirm,
    onCancel,
    type = 'warning'
}) => {
    const getIconColor = () => {
        switch (type) {
            case 'danger':
                return 'text-red-200 bg-red-500/30 border border-red-300/30';
            case 'warning':
                return 'text-yellow-200 bg-yellow-500/30 border border-yellow-300/30';
            case 'info':
                return 'text-blue-200 bg-blue-500/30 border border-blue-300/30';
            default:
                return 'text-gray-200 bg-gray-500/30 border border-gray-300/30';
        }
    };

    const getButtonColor = () => {
        switch (type) {
            case 'danger':
                return 'bg-red-500/80 hover:bg-red-500';
            case 'warning':
                return 'bg-yellow-500/80 hover:bg-yellow-500';
            case 'info':
                return 'bg-blue-500/80 hover:bg-blue-500';
            default:
                return 'bg-gray-500/80 hover:bg-gray-500';
        }
    };
    
    const getIcon = () => {
        switch (type) {
            case 'danger':
                return <ExclamationCircleIcon className="h-6 w-6" aria-hidden="true" />;
            case 'warning':
                return <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />;
            case 'info':
                return <InformationCircleIcon className="h-6 w-6" aria-hidden="true" />;
            default:
                return <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />;
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onCancel}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-indigo-900/70 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white/10 backdrop-blur-xl text-left shadow-2xl transition-all border border-white/20 sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <motion.div 
                                            className={`mx-auto flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-12 sm:w-12 ${getIconColor()}`}
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {getIcon()}
                                        </motion.div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-white">
                                                {title}
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-blue-100">
                                                    {message}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white/5 px-4 py-4 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className={`inline-flex w-full justify-center rounded-full px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-all duration-200 sm:ml-3 sm:w-auto ${getButtonColor()}`}
                                        onClick={onConfirm}
                                    >
                                        {confirmText}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-full bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm shadow-lg ring-1 ring-inset ring-white/20 hover:bg-white/20 transition-all duration-200 sm:mt-0 sm:w-auto"
                                        onClick={onCancel}
                                    >
                                        {cancelText}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default ConfirmDialog; 